<template>
  <div :class="['notes-panel', { 'notes-panel--fill': fill }]">
    <div class="notes-panel__label">{{ label || resx.labels.internal_notes }}</div>
    <!--
      El tamano se arrastra desde la esquina de este div, no del textarea:
      el textarea de Vuetify tiene max-width 100% y su borde vive en el
      contenedor, asi que redimensionarlo a el solo dejaria el borde fijo.
    -->
    <div
      :class="['notes-panel__box', { 'notes-panel__box--fixed': !resizable }]"
      ref="box"
      :style="boxStyle"
      @mousedown="resizable && onResizeStart()"
    >
      <v-textarea
        rows="3"
        no-resize
        hide-details
        outlined
        dense
        :disabled="saving"
        v-model="draft"
      >
      </v-textarea>
    </div>
    <div class="notes-panel__actions" v-if="isDirty">
      <v-btn small text :disabled="saving" @click="cancel()">
        {{ resx.labels.cancel }}
      </v-btn>
      <v-btn small color="primary" :loading="saving" @click="save()">
        {{ resx.labels.save }}
      </v-btn>
    </div>
  </div>
</template>

<script>
"use strict";

import contractorCompanyService from "@/services/ContractorCompanyService";

// { width, height } con ambos lados positivos, o null. Vive fuera del
// componente porque data() lo necesita antes de que existan los methods.
const cloneSize = size => {
  if (!size || !(size.width > 0) || !(size.height > 0)) {
    return null;
  }
  return { width: size.width, height: size.height };
};

export default {
  name: "notesPanel",
  props: {
    contractorId: { type: String },
    // Cual de las tres notas edita este panel: obligations, vehicles o
    // employees. El backend valida el valor contra el mismo whitelist.
    scope: { type: String, required: true },
    value: { type: String, default: "" },
    // { width, height } en px guardado en el servidor, o null para usar el
    // tamano por defecto del CSS.
    size: { type: Object, default: null },
    // Titulo de la caja; si no se pasa, se usa "Notas".
    label: { type: String, default: "" },
    // value => Promise<string>. Si se pasa, reemplaza al guardado de notas de
    // la contratista (por ejemplo para notas de un empleado o vehiculo) y
    // tiene que devolver el texto tal como quedo guardado.
    saveValue: { type: Function, default: null },
    // false: sin manija de arrastre y sin guardar tamano.
    resizable: { type: Boolean, default: true },
    // true: la caja ocupa todo el ancho de su columna en vez de la mitad.
    fill: { type: Boolean, default: false }
  },
  data() {
    return {
      // El borrador vive aparte del valor guardado: comparar los dos es lo
      // que decide si se muestran Guardar y Cancelar.
      draft: "",
      saved: "",
      saving: false,
      // Ultimo tamano aplicado a la caja (el que vino del servidor o el que
      // se acaba de guardar). Es lo que se compara al soltar el mouse para
      // saber si hubo un arrastre real. Se toma de la prop ya en data() para
      // que el primer render salga con el tamano guardado y no haya salto.
      boxSize: cloneSize(this.size)
    };
  },
  computed: {
    isDirty() {
      return this.draft != this.saved;
    },
    boxStyle() {
      if (!this.boxSize) {
        return {};
      }
      return {
        width: `${this.boxSize.width}px`,
        height: `${this.boxSize.height}px`
      };
    }
  },
  watch: {
    // Las vistas cargan la contratista con await despues de montar, asi que
    // el valor real llega despues del primer render.
    value(val) {
      this.reset(val);
    },
    size(val) {
      this.boxSize = cloneSize(val);
    }
  },
  methods: {
    reset(val) {
      this.saved = val || "";
      this.draft = this.saved;
    },
    cancel() {
      this.draft = this.saved;
    },
    async save() {
      if (!this.saveValue && !this.contractorId) {
        return;
      }
      this.saving = true;
      const sent = this.draft;
      try {
        let stored;
        if (this.saveValue) {
          stored = await this.saveValue(sent);
        } else {
          const notes = await contractorCompanyService.editNotes(
            this.contractorId,
            this.scope,
            sent
          );
          stored = notes ? notes[this.scope] : sent;
        }
        // Se toma lo que devolvio el servidor, que ya viene recortado y
        // topeado: si no, un texto sobre el limite quedaria marcado como
        // sucio para siempre porque el borrador nunca igualaria lo guardado.
        this.reset(stored);
        this.$emit("saved", this.saved);
      } catch (error) {
        // http.js ya publica el error; aca solo se deja el borrador intacto
        // para que se pueda reintentar sin volver a escribir la nota.
      } finally {
        this.saving = false;
      }
    },
    // El navegador no avisa cuando termina un arrastre de la esquina, asi
    // que se escucha el mouseup que le sigue a cualquier mousedown sobre la
    // caja. Un click para escribir tambien pasa por aca, pero no cambia el
    // tamano y por eso no guarda nada.
    onResizeStart() {
      window.addEventListener("mouseup", this.onResizeEnd, { once: true });
    },
    onResizeEnd() {
      const dragged = this.draggedSize();
      if (dragged) {
        this.saveSize(dragged);
      }
    },
    // Al arrastrar, el navegador escribe width y height en el style inline
    // de la caja. Se lee eso y no offsetWidth: si la ventana se achico y el
    // max-width recorto la caja, offsetWidth cambiaria sin que el usuario
    // haya tocado nada, y se pisaria el tamano guardado.
    draggedSize() {
      const box = this.$refs.box;
      if (!box) {
        return null;
      }
      const dragged = cloneSize({
        width: parseInt(box.style.width, 10),
        height: parseInt(box.style.height, 10)
      });
      if (!dragged) {
        return null;
      }
      const current = this.boxSize;
      if (
        current &&
        current.width == dragged.width &&
        current.height == dragged.height
      ) {
        return null;
      }
      return dragged;
    },
    async saveSize(size) {
      if (!this.resizable || !this.contractorId) {
        return;
      }
      try {
        const notesSize = await contractorCompanyService.editNotesSize(
          this.contractorId,
          this.scope,
          size
        );
        // Lo que devuelve el servidor ya esta topeado; aplicarlo deja la
        // caja y el estado local alineados con lo guardado.
        this.boxSize = cloneSize(notesSize ? notesSize[this.scope] : size);
      } catch (error) {
        // http.js ya publica el error. La caja queda como la dejo el
        // usuario; el proximo arrastre vuelve a intentar guardar.
      }
    }
  },
  mounted() {
    this.reset(this.value);
  },
  beforeDestroy() {
    window.removeEventListener("mouseup", this.onResizeEnd);
  }
};
</script>

<style lang="scss">
.notes-panel {
  padding: 0 16px 8px 16px;

  &__label {
    font-size: 0.75rem;
    font-weight: 500;
    opacity: 0.7;
    padding-bottom: 4px;
  }

  // resize necesita overflow distinto de visible para dibujar la manija de
  // la esquina. Los minimos no bajan del MIN_SIZE del backend (40px) y el
  // max-width evita que un ancho guardado en un monitor grande desborde la
  // tarjeta en uno chico.
  &__box {
    resize: both;
    overflow: hidden;
    min-width: 120px;
    min-height: 40px;
    max-width: 100%;
    width: 100%;

    @media (min-width: 960px) {
      width: 50%;
    }

    // Sin manija: el ancho lo manda la columna que lo contiene.
    &--fixed {
      resize: none;
    }

    // Con alto explicito en la caja, el textarea de Vuetify tiene que
    // estirarse hasta llenarla; sin alto, sigue usando sus 3 filas.
    .v-input,
    .v-input__control,
    .v-input__slot {
      height: 100%;
    }

    .v-input {
      margin-top: 0;
      padding-top: 0;
    }

    .v-textarea textarea {
      margin-bottom: 6px;
    }
  }

  // Dentro de una columna que ya es media pantalla, la caja llena la columna.
  &--fill &__box {
    width: 100%;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
  }
}
</style>
